import { Box, Container, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { ImageWrapper } from 'components/wrapper/imageWrapper';
import React, { FunctionComponent } from 'react';
import { withTranslations } from 'store/translations';

import { colorScheme } from '../../lib/colorScheme';
import { ExpertBlock as ExpertBlockModel } from '../../models/content-types/expert_block';
import { getButtonWithLinkStyling } from '../base/Button';
import { ContactFormModal } from './ContactFormModal';

interface ExpertBlockProps {
  model: ExpertBlockModel;
}

export const ExpertBlock: FunctionComponent<ExpertBlockProps> = ({ model }) => {
  const { aria } = withTranslations();
  const backgroundColor =
    colorScheme[model.elements.backgroundColor?.value[0].codename as keyof typeof colorScheme]?.base ?? 'white';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const person = model.elements.contact.linkedItems[0].elements;
  const contactModal = model.elements.formModalCtaFormModal.linkedItems[0];
  return (
    <Container
      id={model.system.codename}
      bg={backgroundColor}
      display={'flex'}
      maxWidth='780px'
      w={{ base: 'calc(100% - 40px)', md: '100%' }}
      p='s'
      flexDir='column'
      gap='s'
      alignSelf='center'
    >
      <Text
        as='h2'
        alignSelf={{ base: 'center', md: 'start' }}
        textStyle={{ base: 'desktop.body.book.l', md: 'desktop.h5' }}
      >
        {model.elements.headline.value}{' '}
      </Text>
      <Flex
        flexDir={{ base: 'column-reverse', md: 'row-reverse' }}
        justifyContent={'start'}
        alignItems={{ base: 'center', md: 'unset' }}
        gap={{ base: 'xs', md: 's' }}
      >
        <Flex flexDir='column' alignItems={{ base: 'center', md: 'start' }} w={{ md: 'calc(100% - 200px)' }}>
          <Text aria-label={`${aria('expertName')}: ${person?.displayName}`} textStyle='mobile.body.medium.s'>
            {person.displayName.value}
          </Text>
          <Text aria-label={`${aria('expertOccupation')}: ${person?.jobTitle}`} textStyle='mobile.body.s'>
            {person.jobTitle.value}
          </Text>
          {!!person.phoneNumber.value && (
            <Box my='xs' textStyle='mobile.body.xs' textAlign={{ base: 'center', md: 'start' }}>
              {person.phoneNumber.value}
            </Box>
          )}
          {!!person.description.value && (
            <Box
              my='xs'
              textStyle='mobile.body.xs'
              textAlign={{ base: 'center', md: 'start' }}
              dangerouslySetInnerHTML={{ __html: person.description.value }}
            />
          )}
          {contactModal &&
            getButtonWithLinkStyling(
              `${aria('openContactGetInTouchWith')} ${person?.displayName}`,
              model.elements.formModalCtaButtonText.value,
              onOpen,
              contactModal.system.name,
            )}
          {contactModal && (
            <ContactFormModal
              id={`${model.system.id}${contactModal.system.id}`}
              model={contactModal}
              isOpen={isOpen}
              contactPersonEmail={person.email.value}
              onClose={onClose}
            />
          )}
        </Flex>
        <ImageWrapper
          imageSx={{ w: { base: '100px', md: '160px' }, h: { base: '100px', md: '160px' } }}
          avatarSize={{ base: '2xl', md: '3xl' }}
          imageFrontify={person.frontifyImage.value}
          msImage={person.msImage.value}
          alt={person.displayName.value}
        />
      </Flex>
    </Container>
  );
};
